import React from 'react';
import { observer } from 'mobx-react-lite';
import { Alert, Box, CircularProgress } from '@mui/material';
import ObjectID from 'bson-objectid';
import { Header } from 'components/Header';
import { Footer } from 'components/Footer';

import UnderwritingNavigation, { UnderwritingPageName } from './UnderwritingNavigation';
import { useRootStore } from 'stores/RootStore';
import SubjectProperty from './subject-property';

import {
  ResidentialRentRoll,
  ResidentialRentComps,
  ProjectedResidentialRentRoll,
  CommercialRentRoll,
  CommercialRentComps,
  CommercialReimbursements,
  OtherIncomeContainer,
  TaxesContainer,
  OperatingExpensesContainer,
  ComparableExpensesContainer,
  ExpenseForecastContainer,
  ProFormaContainer,
  ProjectedCommercialRentRoll,
  CapRateConclusion,
} from './income-approach';
import { FinancingTerms } from './financing-terms';
import { SettingsAndExport } from './settings-and-export';

interface UnderwritingPageProps {
  activePage?: UnderwritingPageName;
}

const UnderwritingPage: React.FC<UnderwritingPageProps> = observer(({ activePage }) => {
  const { underwritingPageStore, routeStore } = useRootStore();
  const { underwritingData, loadStatus } = underwritingPageStore;

  const { navigateByName } = routeStore;

  const setNavigation = (routeName: UnderwritingPageName) => {
    const pathParts = window.location.pathname.split('/');
    // Dummy objectID for now...
    const underwritingId = pathParts[2] || new ObjectID().toHexString();

    if (underwritingId) {
      navigateByName(routeName, { id: underwritingId });
    } else {
      console.error('Missing underwriting ID for navigation');
    }
  };

  const renderContent = () => {
    switch (activePage) {
      case 'propertySummary':
        return <SubjectProperty />;
      case 'residentialRentRoll':
        return <ResidentialRentRoll />;
      case 'residentialRentComps':
        return <ResidentialRentComps />;
      case 'residentialProjectedRentRoll':
        return <ProjectedResidentialRentRoll />;
      case 'commercialRentRoll':
        return <CommercialRentRoll />;
      case 'commercialRentComps':
        return <CommercialRentComps />;
      case 'commercialReimbursements':
        return <CommercialReimbursements />;
      case 'commercialProjectedRentRoll':
        return <ProjectedCommercialRentRoll />;
      case 'otherIncome':
        return <OtherIncomeContainer />;
      case 'taxes':
        return <TaxesContainer />;
      case 'expenseHistory':
        return <OperatingExpensesContainer />;
      case 'expenseComps':
        return <ComparableExpensesContainer />;
      case 'expenseForecasts':
        return <ExpenseForecastContainer />;
      case 'proForma':
        return <ProFormaContainer />;
      case 'capRateConclusion':
        return <CapRateConclusion />;
      case 'financingTerms':
        return <FinancingTerms />;
      case 'settingsAndExport':
        return <SettingsAndExport />;
      default:
        return <div>Select a section from the navigation</div>;
    }
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="jobs-container">
      <Header />
      {loadStatus === 'loading' && <CircularProgress sx={{ marginTop: '24px', alignSelf: 'center' }} />}
      {(loadStatus === 'error' || (loadStatus === 'loaded' && !underwritingData)) && (
        <Alert severity="error">There was an error loading this underwriting.</Alert>
      )}
      {loadStatus === 'loaded' && underwritingData && (
        <UnderwritingNavigation setNavigation={setNavigation} renderContent={renderContent} activePage={activePage} />
      )}
      <Footer />
    </Box>
  );
});

export { UnderwritingPage };
